import React, { useState } from 'react';

export default function ContactConsultation() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const response = await fetch('YOUR_FORM_ENDPOINT', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', service: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section id="contact" className="py-24 bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
          backgroundSize: '40px 40px'
        }}></div>
      </div>

      <div className="max-w-[1400px] mx-auto px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Left Content */}
          <div>
            <p className="text-[11px] tracking-[0.3em] uppercase text-[#4A90E2] mb-4 font-light">
              GET IN TOUCH
            </p>
            <h2 className="text-5xl font-serif text-white mb-6 leading-tight">
              <strong>Let's Transform</strong><br />
              Your Space
            </h2>
            <p className="text-base text-gray-300 leading-relaxed mb-10">
              Ready to experience the future of <strong>home automation</strong>? Contact our team for a personalized consultation and discover how we can elevate your property with <strong>Control4, Savant, RTI, or Brilliant</strong> systems.
            </p>

            {/* Contact Info */}
            <div className="space-y-6 mb-10">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#4A90E2]/10 flex items-center justify-center flex-shrink-0">
                  <i className="ri-phone-line text-[#4A90E2] text-xl"></i>
                </div>
                <div>
                  <p className="text-sm text-gray-400 mb-1">Phone</p>
                  <a href="tel:+17862829701" className="text-lg text-white hover:text-[#4A90E2] transition-colors cursor-pointer">
                    (786) 282-9701
                  </a>
                  <p className="text-xs text-gray-400 mt-1">Mon-Thu: 8AM-6PM | Fri: 8AM-3PM</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#4A90E2]/10 flex items-center justify-center flex-shrink-0">
                  <i className="ri-mail-line text-[#4A90E2] text-xl"></i>
                </div>
                <div>
                  <p className="text-sm text-gray-400 mb-1">Email</p>
                  <a href="mailto:moshe@rdmsecurity.com" className="text-lg text-white hover:text-[#4A90E2] transition-colors cursor-pointer">
                    moshe@rdmsecurity.com
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#4A90E2]/10 flex items-center justify-center flex-shrink-0">
                  <i className="ri-map-pin-line text-[#4A90E2] text-xl"></i>
                </div>
                <div>
                  <p className="text-sm text-gray-400 mb-1">Service Area</p>
                  <p className="text-lg text-white">
                    South East Florida<br />
                    West Coast Tampa South
                  </p>
                </div>
              </div>
            </div>

            {/* Emergency Notice */}
            <div className="p-6 bg-[#4A90E2]/10 border border-[#4A90E2]/20 rounded-xl">
              <div className="flex items-start gap-3">
                <i className="ri-alarm-warning-line text-[#4A90E2] text-xl mt-1"></i>
                <div>
                  <h4 className="text-base font-semibold text-white mb-2">Emergency Support</h4>
                  <p className="text-sm text-gray-300">
                    Weekend emergency calls available. Contact us anytime for urgent support needs.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Form */}
          <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#4A90E2] transition-colors text-sm"
                  placeholder="John Smith"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#4A90E2] transition-colors text-sm"
                  placeholder="john@example.com"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-300 mb-2">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#4A90E2] transition-colors text-sm"
                  placeholder="(786) 282-9701"
                />
              </div>

              <div>
                <label htmlFor="service" className="block text-sm font-medium text-gray-300 mb-2">
                  Service Interest *
                </label>
                <select
                  id="service"
                  name="service"
                  value={formData.service}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#4A90E2] transition-colors text-sm cursor-pointer"
                >
                  <option value="" className="bg-gray-900">Select a service</option>
                  <option value="control4" className="bg-gray-900">Control4 Integration</option>
                  <option value="savant" className="bg-gray-900">Savant Systems</option>
                  <option value="rti" className="bg-gray-900">RTI Control</option>
                  <option value="brilliant" className="bg-gray-900">Brilliant Smart Home</option>
                  <option value="fiber" className="bg-gray-900">Fiber Optic Installation</option>
                  <option value="security" className="bg-gray-900">Security Cameras</option>
                  <option value="other" className="bg-gray-900">Other Services</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-2">
                  Project Details
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#4A90E2] transition-colors resize-none text-sm"
                  placeholder="Tell us about your project..."
                ></textarea>
                <p className="text-xs text-gray-400 mt-1">Maximum 500 characters</p>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-8 py-4 bg-[#4A90E2] text-white text-sm font-medium tracking-wide rounded-full hover:bg-[#3A7BC8] hover:shadow-lg hover:shadow-[#4A90E2]/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 whitespace-nowrap"
              >
                {isSubmitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    Sending...
                  </>
                ) : (
                  <>
                    Send Message
                    <i className="ri-send-plane-line"></i>
                  </>
                )}
              </button>

              {submitStatus === 'success' && (
                <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
                  <p className="text-sm text-green-400 flex items-center gap-2">
                    <i className="ri-checkbox-circle-line"></i>
                    Message sent successfully! We'll contact you soon.
                  </p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
                  <p className="text-sm text-red-400 flex items-center gap-2">
                    <i className="ri-error-warning-line"></i>
                    Failed to send message. Please try again or call us directly.
                  </p>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
